import behaviourCustomizedThemeConstants from "@/constants/behaviourCustomizedThemeConstants.js"
import { BEHAVIOUR_TERMINOLOGY_LOCALE_DEFAULTS } from "@/configs/behaviourTerminology.js"

const { LABEL_KEYS, MAX_LABEL_LENGTH, LABEL_PATTERN, RENAME_STATUS } =
  behaviourCustomizedThemeConstants

/**
 * @param {string} [locale]
 * @returns {Record<string, string>}
 */
export function getDefaultThemeLabels(locale) {
  const normalized = String(locale || "en")
    .toLowerCase()
    .startsWith("es")
    ? "es"
    : "en"
  const base =
    BEHAVIOUR_TERMINOLOGY_LOCALE_DEFAULTS[normalized] ||
    BEHAVIOUR_TERMINOLOGY_LOCALE_DEFAULTS.en
  return {
    [LABEL_KEYS.BEHAVIOUR_MANAGEMENT]: base.behaviourManagement || "",
    [LABEL_KEYS.CORE_VALUE]: base.coreValue || "",
    [LABEL_KEYS.POSITIVE_BEHAVIOURS]: base.positiveBehaviours || "",
    [LABEL_KEYS.POINTS]: base.points || ""
  }
}

/**
 * @typedef {Object} CustomizedThemeSettings
 * @property {boolean} renameEnabled
 * @property {Record<string, string>} labels
 */

/**
 * @param {CustomizedThemeSettings | null | undefined} source
 * @returns {CustomizedThemeSettings}
 */
export function cloneCustomizedThemeSettings(source) {
  const base = source || { renameEnabled: false, labels: {} }
  return {
    renameEnabled: Boolean(base.renameEnabled),
    labels: {
      [LABEL_KEYS.BEHAVIOUR_MANAGEMENT]:
        base.labels?.[LABEL_KEYS.BEHAVIOUR_MANAGEMENT] ?? "",
      [LABEL_KEYS.CORE_VALUE]: base.labels?.[LABEL_KEYS.CORE_VALUE] ?? "",
      [LABEL_KEYS.POSITIVE_BEHAVIOURS]:
        base.labels?.[LABEL_KEYS.POSITIVE_BEHAVIOURS] ?? "",
      [LABEL_KEYS.POINTS]: base.labels?.[LABEL_KEYS.POINTS] ?? ""
    }
  }
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidCustomThemeLabel(value) {
  const trimmed = String(value ?? "").trim()
  if (!trimmed || trimmed.length > MAX_LABEL_LENGTH) return false
  return LABEL_PATTERN.test(trimmed)
}

/**
 * @param {CustomizedThemeSettings} settings
 * @returns {boolean}
 */
export function validateCustomizedThemeSettings(settings) {
  if (!settings?.renameEnabled) return true
  return Object.values(LABEL_KEYS).every((key) =>
    isValidCustomThemeLabel(settings.labels?.[key])
  )
}

/**
 * @param {Record<string, string> | null | undefined} overrides
 * @param {string} locale
 * @returns {CustomizedThemeSettings}
 */
export function buildCustomizedThemeSettingsFromStore(overrides, locale) {
  const defaults = getDefaultThemeLabels(locale)
  const hasOverrides =
    overrides &&
    typeof overrides === "object" &&
    Object.values(LABEL_KEYS).some(
      (key) => String(overrides[key] ?? "").trim() !== ""
    )

  if (!hasOverrides) {
    return cloneCustomizedThemeSettings({
      renameEnabled: false,
      labels: { ...defaults }
    })
  }

  return cloneCustomizedThemeSettings({
    renameEnabled: true,
    labels: {
      [LABEL_KEYS.BEHAVIOUR_MANAGEMENT]:
        overrides[LABEL_KEYS.BEHAVIOUR_MANAGEMENT] ||
        defaults[LABEL_KEYS.BEHAVIOUR_MANAGEMENT],
      [LABEL_KEYS.CORE_VALUE]:
        overrides[LABEL_KEYS.CORE_VALUE] || defaults[LABEL_KEYS.CORE_VALUE],
      [LABEL_KEYS.POSITIVE_BEHAVIOURS]:
        overrides[LABEL_KEYS.POSITIVE_BEHAVIOURS] ||
        defaults[LABEL_KEYS.POSITIVE_BEHAVIOURS],
      [LABEL_KEYS.POINTS]:
        overrides[LABEL_KEYS.POINTS] || defaults[LABEL_KEYS.POINTS]
    }
  })
}

/**
 * @param {string} [locale]
 * @returns {boolean}
 */
export function isEnglishLocaleForCustomTheme(locale) {
  return !String(locale || "en")
    .toLowerCase()
    .startsWith("es")
}

/**
 * @param {string} pointsLabel
 * @returns {string}
 */
function derivePointSingular(pointsLabel) {
  const p = String(pointsLabel ?? "").trim()
  if (p.length > 1 && /s$/i.test(p)) {
    return p.slice(0, -1)
  }
  return p
}

/**
 * @param {string} coreValueLabel
 * @returns {string}
 */
function deriveCoreValuesLower(coreValueLabel) {
  return String(coreValueLabel ?? "")
    .trim()
    .toLowerCase()
}

/**
 * @param {CustomizedThemeSettings} settings
 * @returns {Record<string, string> | null}
 */
export function buildTerminologyOverridesFromCustomizedTheme(settings) {
  if (!settings?.renameEnabled) return null

  const labels = settings.labels || {}
  const behaviourManagement = String(
    labels[LABEL_KEYS.BEHAVIOUR_MANAGEMENT] ?? ""
  ).trim()
  const coreValue = String(labels[LABEL_KEYS.CORE_VALUE] ?? "").trim()
  const positiveBehaviours = String(
    labels[LABEL_KEYS.POSITIVE_BEHAVIOURS] ?? ""
  ).trim()
  const points = String(labels[LABEL_KEYS.POINTS] ?? "").trim()
  const point = derivePointSingular(points)
  const coreValuesLower = deriveCoreValuesLower(coreValue)

  return {
    behaviourManagement,
    behaviourManagementSettings: `${behaviourManagement} Settings`,
    coreValue,
    coreValuesLower,
    positiveBehaviours,
    point,
    points,
    colPoints: points,
    tabsAriaLabel: `${behaviourManagement} settings sections`,
    coreValueSectionTitle: `${coreValue} settings`,
    addCoreValue: `Add ${coreValue}`,
    positiveBehavioursSectionTitle: `${positiveBehaviours} settings`,
    addPositiveBehaviour: `Add ${positiveBehaviours}`
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} apiData
 * @returns {boolean}
 */
export function resolveRenameEnabledFromApi(apiData) {
  const status = apiData?.status
  if (status != null && status !== "") {
    const n = Number(status)
    if (Number.isFinite(n)) {
      return n === RENAME_STATUS.ENABLED
    }
  }

  const rawRenameEnabled = apiData?.rename_enabled
  if (rawRenameEnabled != null && rawRenameEnabled !== "") {
    const n = Number(rawRenameEnabled)
    if (Number.isFinite(n)) {
      return n === RENAME_STATUS.ENABLED
    }
    if (rawRenameEnabled === true) return true
    if (rawRenameEnabled === false) return false
  }

  return false
}

/**
 * @param {Record<string, unknown> | null | undefined} apiData
 * @param {Record<string, string>} defaults
 * @returns {Record<string, string>}
 */
export function mapApiCustomizedThemeLabels(apiData, defaults) {
  return {
    [LABEL_KEYS.BEHAVIOUR_MANAGEMENT]:
      String(apiData?.behaviour_management_name ?? "").trim() ||
      defaults[LABEL_KEYS.BEHAVIOUR_MANAGEMENT],
    [LABEL_KEYS.CORE_VALUE]:
      String(apiData?.core_value_name ?? "").trim() ||
      defaults[LABEL_KEYS.CORE_VALUE],
    [LABEL_KEYS.POSITIVE_BEHAVIOURS]:
      String(apiData?.positive_behaviours_name ?? "").trim() ||
      defaults[LABEL_KEYS.POSITIVE_BEHAVIOURS],
    [LABEL_KEYS.POINTS]:
      String(apiData?.points_name ?? "").trim() || defaults[LABEL_KEYS.POINTS]
  }
}

/**
 * @param {Record<string, string>} labels
 * @param {Record<string, string>} defaults
 * @returns {{ behaviour_management_name: string, core_value_name: string, positive_behaviours_name: string, points_name: string }}
 */
export function buildCustomizedThemeLabelPayload(labels, defaults) {
  const source = labels || {}
  const fallback = defaults || {}
  return {
    behaviour_management_name:
      String(source[LABEL_KEYS.BEHAVIOUR_MANAGEMENT] ?? "").trim() ||
      fallback[LABEL_KEYS.BEHAVIOUR_MANAGEMENT] ||
      "",
    core_value_name:
      String(source[LABEL_KEYS.CORE_VALUE] ?? "").trim() ||
      fallback[LABEL_KEYS.CORE_VALUE] ||
      "",
    positive_behaviours_name:
      String(source[LABEL_KEYS.POSITIVE_BEHAVIOURS] ?? "").trim() ||
      fallback[LABEL_KEYS.POSITIVE_BEHAVIOURS] ||
      "",
    points_name:
      String(source[LABEL_KEYS.POINTS] ?? "").trim() ||
      fallback[LABEL_KEYS.POINTS] ||
      ""
  }
}

/**
 * @param {CustomizedThemeSettings} settings
 * @returns {Record<string, string> | null}
 */
export function themeSettingsToTerminologyOverrides(settings) {
  return buildTerminologyOverridesFromCustomizedTheme(settings)
}

/**
 * @param {Record<string, unknown> | null | undefined} apiData
 * @param {string} [locale]
 * @returns {CustomizedThemeSettings}
 */
export function mapApiCustomizedThemeToForm(apiData, locale) {
  const defaults = getDefaultThemeLabels(locale)
  const renameEnabled = resolveRenameEnabledFromApi(apiData)

  return cloneCustomizedThemeSettings({
    renameEnabled,
    labels: mapApiCustomizedThemeLabels(apiData, defaults)
  })
}

/**
 * @param {CustomizedThemeSettings} form
 * @param {string} [locale]
 * @returns {{ status: number, rename_enabled: boolean, behaviour_management_name: string, core_value_name: string, positive_behaviours_name: string, points_name: string }}
 */
export function buildCustomizedThemeUpsertPayload(form, locale) {
  const defaults = getDefaultThemeLabels(locale)
  const renameEnabled = Boolean(form?.renameEnabled)
  const labelPayload = buildCustomizedThemeLabelPayload(form?.labels, defaults)

  return {
    status: renameEnabled ? RENAME_STATUS.ENABLED : RENAME_STATUS.DISABLED,
    rename_enabled: renameEnabled,
    ...labelPayload
  }
}

/**
 * Terminology overrides from API (EN only; ES always uses locale defaults).
 * @param {Record<string, unknown> | null | undefined} apiData
 * @param {string} [locale]
 * @returns {Record<string, string> | null}
 */
export function customizedThemeApiToTerminologyOverrides(apiData, locale) {
  if (!isEnglishLocaleForCustomTheme(locale)) {
    return null
  }
  return buildTerminologyOverridesFromCustomizedTheme(
    mapApiCustomizedThemeToForm(apiData, locale)
  )
}

/**
 * @param {unknown} err
 * @returns {string}
 */
export function parseCustomizedThemeApiError(err) {
  const data = err?.response?.data
  return String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""
}
